import React, { useState } from 'react';
import styled from '@emotion/styled';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import { mediaQuery, Spacings } from '@styles/index';
import KeyboardArrowUp from '@icon/KeyboardArrowUp';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import { useTestSeriesData } from './useTestSeriesData';

const FAQSectionWrapper = styled.div`
  display: flex;
  background-color: var(--color-base-0);
  scroll-margin-top: 100px;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  width: 100%;
  padding: 100px 20%;

  ${mediaQuery.uptoMobile} {
    padding: 40px 5%;
    gap: 8px;
  }
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 64px;
  line-height: 120%;
  letter-spacing: -2%;
  text-align: left;
  color: var(--color-primary-focus);

  ${mediaQuery.uptoMobile} {
    font-size: 40px;
  }
`;

const SubTitle = styled.p`
  text-align: left;
  font-weight: 400;
  font-size: 32px;
  line-height: 150%;
  letter-spacing: -1px;
  color: var(--color-text-secondary);

  ${mediaQuery.uptoMobile} {
    font-size: 18px;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 40px;

  ${mediaQuery.uptoMobile} {
    margin-top: 24px;
    gap: 8px;
  }
`;

const FAQItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const FAQQuestion = styled.p`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.24px;
  width: 100%;
  color: var(--color-divider-tertiary);
`;

const StyledAccordion = styled(Accordion)`
  background-color: var(--color-background-primary);
  border-radius: 8px;
  box-shadow: none;
  border-bottom: 1px solid var(--color-divider);
  padding: 12px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;

  ${mediaQuery.uptoMobile} {
    padding: 8px;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  padding: 0;
  display: flex !important;
  align-items: center;
  justify-content: space-between;
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 0;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.24px;
  color: var(--color-divider-tertiary);
`;

const ArrowIconWrapper = styled.span`
  display: flex;
  align-items: flex-start;
  margin-left: 8px;
`;

const FAQSection = () => {
  const { data } = useTestSeriesData();
  const [expanded, setExpanded] = useState<number | false>(false);

  if (!data?.faqCard) return null;
  const { faqCard, faqSectionTitle, faqSectionSubtitle } = data;

  const handleChange =
    (panel: number) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <FAQSectionWrapper id="faqs">
      <Title>{faqSectionTitle}</Title>
      <SubTitle>{faqSectionSubtitle}</SubTitle>
      <FAQList>
        {faqCard.map((faq, index) => (
          <FAQItem key={faq.question + index}>
            <StyledAccordion
              expanded={expanded === index}
              onChange={handleChange(index)}
            >
              <StyledAccordionSummary
                aria-controls={`faq-content-${index}`}
                id={`faq-header-${index}`}
              >
                <FAQQuestion>{faq.question}</FAQQuestion>
                <ArrowIconWrapper>
                  {expanded === index ? (
                    <KeyboardArrowUp
                      size={Spacings.SPACING_5B}
                      fill="var(--color-text-secondary)"
                    />
                  ) : (
                    <KeyboardArrowDown
                      size={Spacings.SPACING_5B}
                      fill="var(--color-text-secondary)"
                    />
                  )}
                </ArrowIconWrapper>
              </StyledAccordionSummary>
              <StyledAccordionDetails>{faq.answer}</StyledAccordionDetails>
            </StyledAccordion>
          </FAQItem>
        ))}
      </FAQList>
    </FAQSectionWrapper>
  );
};

export default FAQSection;
